import { commonActions, apiActions } from '../app/actions';
import { fireMessageEvent } from '../messenger/utils';
import { makeRequest } from '../lib/fetch';
import { setNotificationBarToOpen } from '../ui/actions';
import { batch } from 'react-redux';
import { analyticActionMap } from '../analytics/utils';
import {
  HOCKEY_CHALLENGE_USE_CASE,
  isPlatformTransportFailure,
  isValidPlatformQuestion,
  shouldAllowPlay,
  shouldFallbackFromPlatformError,
  shouldShowLockout,
} from '../stq/platformQuestion';

const { skillTestAnswered, path, answerElement, correctValue, incorrectValue } = analyticActionMap;

const checkIfPlayoffs = async (config) => {
  const { apiUrl, graphqlUrl, language, platform, userId, env, currentSession } = config;

  const response = await makeRequest({
    method: 'GET',
    apiUrl,
    graphqlUrl,
    language,
    platform,
    userId,
    env,
    currentSession,
    route: 'postSeasonLeaderboard',
  });

  return response.status === 'current';
};

export const setCompletedOnboarding = (config) => (dispatch) => {
  const { platform, env } = config;
  const { setValue } = commonActions('completed_onboarding');
  dispatch(setValue());
  // Fire mParticle event, this will trigger Braze Onboarding Modal
  // We want to trigger it here, since there are two in-repo onboarding
  // screens between the STQ and the Braze message this triggers
  fireMessageEvent({
    platform,
    env,
    title: skillTestAnswered,
    data: { path: path, element_name: `${answerElement}_${correctValue}`, element_action: correctValue },
  });
};

export const setSelectedAnswer = (answer) => (dispatch) => {
  const { setValue } = commonActions('selected_answer');
  dispatch(setValue(answer));
};

export const setCorrectAnswer = () => (dispatch) => {
  const { setValue } = commonActions('correct_answer');
  dispatch(setValue());
};

export const setIncorrectAnswer = () => (dispatch) => {
  const { setValue } = commonActions('incorrect_answer');
  dispatch(setValue());
};

export const setLockout = () => (dispatch) => {
  const { setValue } = commonActions('lockout');
  dispatch(setValue());
};

export const setPlatformQuestion = (question) => (dispatch) => {
  const { setValue } = commonActions('platform_question');
  dispatch(setValue(question));
};

export const setUseLegacyStq = () => (dispatch) => {
  const { setValue } = commonActions('use_legacy_stq');
  dispatch(setValue());
};

export const setNotificationStatus = (status) => (dispatch) => {
  const { setValue } = commonActions('notifications_status');
  dispatch(setValue(status));
};

export const setNotificationToggle = (status) => (dispatch) => {
  const { setValue } = commonActions('toggle');
  dispatch(setValue(status));
};

const applyPlatformLoad = (question, fetchSuccess) => (dispatch) => {
  if (shouldAllowPlay(question.outcome)) {
    return dispatch(fetchSuccess(true));
  }
  if (shouldShowLockout(question.outcome)) {
    dispatch(setLockout());
    return dispatch(fetchSuccess(false));
  }
  dispatch(setPlatformQuestion(question));
  return dispatch(fetchSuccess(false));
};

const fetchLegacyQuizData = async ({ dispatch, config, fetchSuccess, fetchFailure }) => {
  dispatch(setUseLegacyStq());
  try {
    const { apiUrl, graphqlUrl, language, platform, env, userId, currentSession } = config;
    const isPlayoffs = await checkIfPlayoffs(config);
    dispatch({ type: 'SET_IS_PLAYOFFS', payload: isPlayoffs });
    const eventId = isPlayoffs ? '2026-27-playoffs' : '2026-27';

    const result = await makeRequest({
      method: 'GET',
      route: 'quiz',
      params: { eventId },
      apiUrl,
      graphqlUrl,
      language,
      platform,
      env,
      userId,
      currentSession,
    });

    if (result === 'error') {
      return dispatch(fetchFailure('error'));
    }
    return dispatch(fetchSuccess(result));
  } catch (error) {
    if (isPlatformTransportFailure(error)) {
      return dispatch(fetchSuccess(false));
    }
    throw error;
  }
};

export const fetchQuizData = () => async (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, graphqlUrl, language, platform, env, userId, currentSession } = config;
  const { fetchStart, fetchSuccess, fetchFailure } = apiActions('quiz_data');

  dispatch(fetchStart());

  try {
    if (graphqlUrl) {
      try {
        const platformResult = await makeRequest({
          method: 'GET',
          route: 'skillTestingQuestion',
          apiUrl,
          graphqlUrl,
          language,
          platform,
          env,
          userId,
          currentSession,
        });

        if (isValidPlatformQuestion(platformResult)) {
          return dispatch(applyPlatformLoad(platformResult, fetchSuccess));
        }
      } catch (platformError) {
        if (!shouldFallbackFromPlatformError(platformError) && !isPlatformTransportFailure(platformError)) {
          return dispatch(fetchFailure(platformError));
        }
      }
    }

    return await fetchLegacyQuizData({ dispatch, config, fetchSuccess, fetchFailure });
  } catch (error) {
    if (error.code === 'NotAllowed') {
      return dispatch(setLockout());
    }
    return dispatch(fetchFailure(error));
  }
};

const applyPlatformSubmit =
  (result, sendSuccess, { platform, env }) =>
  (dispatch) => {
    if (shouldAllowPlay(result?.outcome)) {
      fireMessageEvent({
        platform,
        env,
        title: skillTestAnswered,
        data: { path: path, element_name: `${answerElement}_${correctValue}`, element_action: correctValue },
      });
      dispatch(setCorrectAnswer());
      return dispatch(sendSuccess(result));
    }

    if (shouldShowLockout(result?.outcome)) {
      return dispatch(setLockout());
    }

    fireMessageEvent({
      platform,
      env,
      title: skillTestAnswered,
      data: { path: path, element_name: `${answerElement}_${incorrectValue}`, element_action: incorrectValue },
    });
    dispatch(setNotificationBarToOpen());
    return dispatch(setIncorrectAnswer());
  };

export const sendQuizAnswer = () => async (dispatch, getState) => {
  const { config, onboarding } = getState();
  const { selectedAnswer, attempts, answers, platformQuestion } = onboarding;
  const { apiUrl, graphqlUrl, language, env, userId, currentSession, platform } = config;

  const { sendStart, sendSuccess, sendFailure } = apiActions('quiz_answer');

  dispatch(sendStart());

  try {
    if (graphqlUrl && isValidPlatformQuestion(platformQuestion)) {
      if (shouldAllowPlay(platformQuestion.outcome)) {
        dispatch(setCorrectAnswer());
        return dispatch(sendSuccess(platformQuestion));
      }

      const result = await makeRequest({
        route: 'skillTestingAnswer',
        data: {
          useCase: HOCKEY_CHALLENGE_USE_CASE,
          choiceId: selectedAnswer,
        },
        apiUrl,
        graphqlUrl,
        language,
        platform,
        env,
        userId,
        currentSession,
      });

      return dispatch(applyPlatformSubmit(result, sendSuccess, { platform, env }));
    }

    const isPlayoffs = await checkIfPlayoffs(config);
    const eventId = isPlayoffs ? '2026-27-playoffs' : '2026-27';

    const isIncorrect = selectedAnswer !== answers[attempts];

    const result = await makeRequest({
      route: 'quiz',
      data: { eventId, failed: isIncorrect },
      apiUrl,
      graphqlUrl,
      language,
      platform,
      env,
      userId,
      currentSession,
    });

    if (!result) {
      fireMessageEvent({
        platform,
        env,
        title: skillTestAnswered,
        data: { path: path, element_name: `${answerElement}_${incorrectValue}`, element_action: incorrectValue },
      });
      dispatch(setNotificationBarToOpen());
      return dispatch(setIncorrectAnswer());
    }

    fireMessageEvent({
      platform,
      env,
      title: skillTestAnswered,
      data: { path: path, element_name: `${answerElement}_${correctValue}`, element_action: correctValue },
    });

    dispatch(setCorrectAnswer());
    return dispatch(sendSuccess(result));
  } catch (error) {
    if (error.code === 'NotAllowed') {
      return dispatch(setLockout());
    }
    return dispatch(sendFailure(error));
  }
};

export const resetUserNameStatus = () => {
  return { type: 'RESET_USERNAME_STATUS' };
};
export const resetErrorCodeStatus = () => {
  return { type: 'SEND_ERROR_CODE_RESET' };
};

export const resetUsernameAttemptFailure = () => {
  return { type: 'SET_USERNAME_ATTEMPT_FAILURE' };
};

export const resetUsernameAttemptSuccess = () => {
  return { type: 'SET_USERNAME_ATTEMPT_SUCCESS' };
};

export const submitUsername = (username) => async (dispatch, getState) => {
  dispatch(setUsernameValidationErr(false));
  const { config } = getState();
  const { apiUrl, graphqlUrl, language, platform, env, userId, currentSession } = config;

  const trimmedUsername = username.trim();
  const regex = new RegExp(/^\w{3,15}$/i);
  if (!regex.test(trimmedUsername)) {
    return dispatch(setUsernameValidationErr(true));
  }

  const { sendStart, sendSuccess, sendFailure } = apiActions('username');
  const { fetchFailure } = apiActions('auth');
  dispatch(sendStart());

  try {
    const result = await makeRequest({
      method: 'PATCH',
      route: 'user',
      data: { username: trimmedUsername },
      apiUrl,
      graphqlUrl,
      language,
      platform,
      env,
      userId,
      currentSession,
    });
    dispatch(fetchUserData());

    setTimeout(() => {
      dispatch(resetUserNameStatus());
    }, 10000);

    return batch(() => {
      dispatch(sendSuccess({ ...result, username: trimmedUsername }));
      dispatch(resetUsernameAttemptSuccess());
    });
  } catch (error) {
    if (error.code && error.code.includes('hc.')) {
      return dispatch(sendFailure(error.code));
    }
    if (error.status === 401) {
      return batch(() => {
        dispatch(fetchFailure());
        dispatch(resetUsernameAttemptFailure());
      });
    }
    return dispatch(sendFailure(error));
  }
};

export const fetchUserData = () => async (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, graphqlUrl, language, platform, env, userId, currentSession } = config;

  const { fetchStart, fetchSuccess, fetchFailure } = apiActions('user');

  dispatch(fetchStart());

  try {
    const result = await makeRequest({
      method: 'GET',
      route: 'user',
      apiUrl,
      graphqlUrl,
      language,
      platform,
      env,
      userId,
      currentSession,
    });

    return dispatch(fetchSuccess(result));
  } catch (error) {
    return dispatch(fetchFailure(error));
  }
};

export const setUsername = (username) => (dispatch) => {
  const { setValue } = commonActions('username');
  dispatch(setValue(username));
};

export const setUsernameValidationErr = (isErr) => (dispatch) => {
  const { setValue } = commonActions('username_validation_err');
  dispatch(setValue(isErr));
};

export const fetchNotificationStatus = () => async (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, graphqlUrl, language, platform, env, userId, currentSession } = config;
  const { fetchStart, fetchSuccess, fetchFailure } = apiActions('notifications');

  dispatch(fetchStart());

  try {
    const result = await makeRequest({
      method: 'GET',
      route: 'notification',
      apiUrl,
      graphqlUrl,
      language,
      platform,
      env,
      userId,
      currentSession,
    });

    return dispatch(fetchSuccess(result));
  } catch (error) {
    return dispatch(fetchFailure(error));
  }
};

export const sendNotificationStatus = (status) => async (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, graphqlUrl, language, platform, env, userId, currentSession } = config;

  const { sendStart, sendSuccess, sendFailure } = apiActions('notifications');

  dispatch(sendStart());

  try {
    await makeRequest({
      route: 'notification',
      data: {
        attributes: { tnhc_user_reminders: status },
      },
      apiUrl,
      graphqlUrl,
      language,
      platform,
      env,
      userId,
      currentSession,
    });

    return dispatch(sendSuccess(status));
  } catch (error) {
    return dispatch(sendFailure(error));
  }
};
